import { useState, useEffect, useCallback, useRef } from 'react';

// Constants
const DEFAULT_THROTTLE_DELAY = 16; // ~60fps
const DEFAULT_DEBOUNCE_DELAY = 150;

// Create throttle function with proper cleanup
const createThrottle = (func, delay) => {
  let timeoutId;
  let lastExecTime = 0;
  
  const throttledFunc = function (...args) {
    const currentTime = Date.now();
    
    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };

  // Add cleanup method
  throttledFunc.cancel = () => {
    clearTimeout(timeoutId);
    timeoutId = null;
  };

  return throttledFunc;
};

// Create debounce function with proper cleanup
const createDebounce = (func, delay) => {
  let timeoutId;
  
  const debouncedFunc = function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };

  // Add cleanup method
  debouncedFunc.cancel = () => {
    clearTimeout(timeoutId);
    timeoutId = null;
  };

  return debouncedFunc;
};

export const useOptimizedScroll = (options = {}) => {
  const {
    throttleDelay = DEFAULT_THROTTLE_DELAY,
    debounceDelay = DEFAULT_DEBOUNCE_DELAY,
    useThrottle = true
  } = options;

  const [scrollY, setScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const frameRef = useRef();
  const handlerRef = useRef();

  const updateScrollState = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    
    frameRef.current = requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      setIsScrolled(currentScrollY > 0);
    });
  }, []);

  useEffect(() => {
    // Create optimized handler with proper cleanup
    const optimizedHandler = useThrottle 
      ? createThrottle(updateScrollState, throttleDelay)
      : createDebounce(updateScrollState, debounceDelay);

    handlerRef.current = optimizedHandler;

    window.addEventListener('scroll', optimizedHandler, { passive: true });
    
    // Set initial scroll state
    updateScrollState();
    
    return () => {
      window.removeEventListener('scroll', optimizedHandler);
      
      // Cleanup throttle/debounce timers
      if (optimizedHandler.cancel) {
        optimizedHandler.cancel();
      }
      
      // Cleanup animation frame
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [updateScrollState, throttleDelay, debounceDelay, useThrottle]);

  return { scrollY, isScrolled };
};